import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cn from '../../lib/cn';
import site from '../../data/site';
import navLinks from '../../data/nav';
import Logo from './Logo';
import MobileMenu from './MobileMenu';
import Icon from '../ui/Icon';
import Button from '../ui/Button';
import useScrollPosition from '../../hooks/useScrollPosition';
import useActiveSection from '../../hooks/useActiveSection';

const SECTION_IDS = navLinks.map((link) => link.id);

/**
 * Sticky navigation bar. It condenses and gains a translucent background once
 * the page moves, and carries a hairline reading-progress indicator.
 */
export function Header() {
  const { scrolled, progress } = useScrollPosition(40);
  const active = useActiveSection(SECTION_IDS);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // The nav points at sections of the home page. Away from home (the legal
  // pages, a 404) those anchors have to route home first.
  const onHome = pathname === '/';
  const sectionHref = (id) => (onHome ? `#${id}` : `/#${id}`);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50">
        {/* Main bar. */}
        <div
          className={cn(
            'border-b transition-all duration-500 ease-silk',
            scrolled
              ? 'border-ink-950/10 bg-canvas/90 shadow-[0_10px_30px_-24px_rgba(7,15,54,0.5)] backdrop-blur-xl'
              : 'border-transparent bg-canvas'
          )}
        >
          <div
            className={cn(
              'shell flex items-center justify-between gap-6 transition-all duration-500 ease-silk',
              scrolled ? 'h-16' : 'h-20'
            )}
          >
            <Link to="/" aria-label={`${site.shortName} — home`} className="shrink-0">
              <Logo markSize={scrolled ? 36 : 44} />
            </Link>

            <nav aria-label="Primary" className="hidden lg:block">
              <ul className="flex items-center gap-1">
                {navLinks.map((link) => {
                  const isActive = onHome && active === link.id;
                  return (
                    <li key={link.id}>
                      <a
                        href={sectionHref(link.id)}
                        aria-current={isActive ? 'true' : undefined}
                        className={cn(
                          'relative px-4 py-2 font-sans text-[0.78rem] uppercase tracking-widest transition-colors duration-300',
                          isActive ? 'text-ink-950' : 'text-ink-700/70 hover:text-ink-950'
                        )}
                      >
                        {link.label}
                        <span
                          className={cn(
                            'absolute inset-x-4 -bottom-0.5 h-px origin-left bg-sunrise transition-transform duration-400 ease-silk',
                            isActive ? 'scale-x-100' : 'scale-x-0'
                          )}
                        />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="flex items-center gap-2">
              <Button
                href={site.contact.whatsappHref}
                variant="solid"
                size="sm"
                className="hidden sm:inline-flex"
              >
                <Icon name="whatsapp" size={16} />
                Enquire
              </Button>

              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={menuOpen}
                className="grid h-11 w-11 place-items-center rounded-full border border-ink-900/15 text-ink-900 transition hover:border-ink-900/50 lg:hidden"
              >
                <Icon name="menu" />
              </button>
            </div>
          </div>
        </div>

        {/* Reading progress — a thin sunrise line along the bottom edge. */}
        <div
          className="h-0.5 origin-left bg-sunrise transition-opacity duration-300"
          style={{ transform: `scaleX(${progress})`, opacity: scrolled ? 1 : 0 }}
          aria-hidden="true"
        />
      </header>

      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        active={onHome ? active : null}
        sectionHref={sectionHref}
      />
    </>
  );
}

export default Header;
